import {
	BackgroundOption,
	Cover,
	CoverOption,
	CoverType,
	Logo,
} from "@giga-user-fern/api/types/api";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
	AudioTrack,
	PlatformDetails,
} from "@giga-user-fern/api/types/api/resources/organizations";

type PlatformDetailsState = {
	value: PlatformDetails | null;
};

const initialState: PlatformDetailsState = {
	value: null,
};

export const platformDetailSlice = createSlice({
	name: "platformDetails",
	initialState,
	reducers: {
		addAudioTrack: (state, action: PayloadAction<AudioTrack>) => {
			if (state.value?.audioTracks)
				state.value.audioTracks.push(action.payload);
			else {
				state.value = {
					...state.value,
					audioTracks: [action.payload],
				};
			}
		},

		deleteAudioTrack: (state, action: PayloadAction<AudioTrack>) => {
			if (state.value?.audioTracks) {
				state.value.audioTracks = state.value.audioTracks.filter(
					(track) => track.src !== action.payload.src,
				);

				if (state.value.favoriteAudioTracks) {
					state.value.favoriteAudioTracks =
						state.value.favoriteAudioTracks.filter(
							(track) => track.src !== action.payload.src,
						);
				}
			}
		},

		addAudioTrackToFavorites: (
			state,
			action: PayloadAction<AudioTrack>,
		) => {
			if (state.value?.favoriteAudioTracks)
				state.value.favoriteAudioTracks.push(action.payload);
			else {
				state.value = {
					...state.value,
					favoriteAudioTracks: [action.payload],
				};
			}
		},

		removeAudioTrackFromFavorites: (
			state,
			action: PayloadAction<AudioTrack>,
		) => {
			if (state.value?.favoriteAudioTracks)
				state.value.favoriteAudioTracks =
					state.value.favoriteAudioTracks.filter(
						(track) => track.src !== action.payload.src,
					);
		},

		addLogo: (state, action: PayloadAction<Logo>) => {
			if (state.value?.logos) state.value.logos.push(action.payload);
		},

		setPlatformDetails: (state, action: PayloadAction<PlatformDetails>) => {
			state.value = action.payload;
		},

		addCover: (
			state,
			action: PayloadAction<{
				coverOption: CoverOption;
				coverType: CoverType;
			}>,
		) => {
			const { coverOption, coverType } = action.payload;
			const list =
				coverType === "intro" ? state.value?.intro : state.value?.outro;

			if (list) {
				//there are already some intro options
				list.options = [...list.options, coverOption];
			} else {
				//there are no intro options
				const new_options = {
					default: coverOption.id,
					options: [coverOption],
				};

				state.value = {
					...state.value,
					intro:
						coverType === "intro"
							? new_options
							: state.value?.intro,
					outro:
						coverType === "outro"
							? new_options
							: state.value?.outro,
				};
			}
		},

		addCoverV2: (
			state,
			action: PayloadAction<{
				coverOption: CoverOption;
				coverType: CoverType;
			}>,
		) => {
			const { coverOption, coverType } = action.payload;
			const list =
				coverType === "intro" ? state.value?.intro : state.value?.outro;

			if (list) {
				//there are already some intro options
				list.options = [...list.options, coverOption];
			} else {
				//there are no intro options
				const new_options = {
					default: coverOption.id,
					options: [coverOption],
				};

				state.value = {
					...state.value,
					intro:
						coverType === "intro"
							? new_options
							: state.value?.intro,
					outro:
						coverType === "outro"
							? new_options
							: state.value?.outro,
				};
			}
		},

		deleteCover: (
			state,
			action: PayloadAction<{
				coverOption: CoverOption;
				coverType: CoverType;
			}>,
		) => {
			const { coverOption, coverType } = action.payload;
			const list =
				coverType === "intro" ? state.value?.intro : state.value?.outro;

			if (list?.options) {
				//there are already some intro options
				if (list.options.length === 1) {
					//don't delete the last option
					return;
				}

				list.options = list.options.filter(
					(option) => option.id !== coverOption.id,
				);
			}
		},

		deleteCoverV2: (
			state,
			action: PayloadAction<{
				coverOption: CoverOption;
				coverType: CoverType;
			}>,
		) => {
			const { coverOption, coverType } = action.payload;
			const list =
				coverType === "intro" ? state.value?.intro : state.value?.outro;

			if (list) {
				list.options = list.options.filter(
					(option) => option.id !== coverOption.id,
				);

				if (list.options.length === 0) {
					state.value = {
						...state.value,
						[coverType]: null,
					};
				}
			}

			if (state.value?.intro === null && state.value?.outro === null) {
				state.value = null;
			}

			return;
		},

		updateCover: (
			state,
			action: PayloadAction<{
				coverOption: CoverOption;
				coverType: CoverType;
			}>,
		) => {
			const { coverOption, coverType } = action.payload;
			const list =
				coverType === "intro" ? state.value?.intro : state.value?.outro;

			if (list?.options) {
				list.options = list.options.map((option) => {
					if (option.id === coverOption.id) {
						return coverOption;
					}
					return option;
				});
			}
		},

		addBackground: (state, action: PayloadAction<BackgroundOption>) => {
			const backgroundOption = action.payload;
			const list = state.value?.background;

			if (list) {
				//there are already some background options
				list.options = [...list.options, backgroundOption];
			} else {
				//there are no bg options
				const new_options = {
					default: backgroundOption.id,
					options: [backgroundOption],
				};

				state.value = {
					...state.value,
					background: new_options,
				};
			}
		},

		deleteBackground: (state, action: PayloadAction<BackgroundOption>) => {
			const bgOption = action.payload;
			const list = state.value?.background;

			if (list?.options) {
				//there are already some intro options

				if (list.options.length === 1) {
					//don't delete the last option
					return;
				}

				list.options = list.options.filter(
					(option) => option.id !== bgOption.id,
				);
			}
		},
	},
});

export const {
	addBackground,
	deleteBackground,
	addAudioTrack,
	deleteAudioTrack,
	addAudioTrackToFavorites,
	removeAudioTrackFromFavorites,
	updateCover,
	setPlatformDetails,
	addLogo,
	addCover,
	deleteCover,
} = platformDetailSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export default platformDetailSlice.reducer;

export const selectAudioTracks = (state: RootState) =>
	state.platformDetails?.value?.audioTracks || [];
export const selectFavoriteAudioTracks = (state: RootState) =>
	state.platformDetails?.value?.favoriteAudioTracks || [];
export const selectLogos = (state: RootState) =>
	state.platformDetails?.value?.logos || [];
export const selectPlatformDetails = (state: RootState) =>
	state.platformDetails;
export const selectOrganizationLogos = (state: RootState) =>
	state.platformDetails?.value?.organizationLogos || [];
export const selectOrganizationBackgrounds = (state: RootState) =>
	state.platformDetails?.value?.organizationBackgrounds || [];
export const selectPremadeBackgrounds = (state: RootState) =>
	state.platformDetails?.value?.premadeBackgrounds || [];
export const selectOrganizationTemplates = (state: RootState) =>
	state.platformDetails?.value?.organizationTemplates || [];
export const selectPremadeTemplates = (state: RootState) =>
	state.platformDetails?.value?.premadeTemplates || [];
export const selectDefaultIntroTemplate = (state: RootState) =>
	state.platformDetails?.value?.defaultIntro || null;
export const selectDefaultOutroTemplate = (state: RootState) =>
	state.platformDetails?.value?.defaultOutro || null;
export const selectDefaultBackground = (state: RootState) =>
	state.platformDetails?.value?.defaultBackground || null;
